import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { type SerializedError } from '@reduxjs/toolkit';
import { type SessionTestResponse } from '../webrtc/session';
import { CustomError } from '@doxyme/api-services';
import { formatFetchBaseQueryError } from './formatFetchBaseQueryError';
import { WebRtcSessionV2 } from './graphQlTypes.generated';

type SessionApiError = {
  error: string;
  message: string | string[];
};

const isSessionApiError = (object: unknown): object is SessionApiError => {
  return (
    typeof object === 'object' &&
    object !== null &&
    typeof (object as SessionApiError).error === 'string'
  );
};

const formatSessionApiErrorToString = (
  sessionApiError: SessionApiError,
): string => {
  const { error: category, message } = sessionApiError;
  const messages = [message].flat();
  return `${category} (${messages.join('; ')})`;
};

export type SessionResponseData = {
  data: WebRtcSessionV2;
};
type SessionTestResponseData = { data: SessionTestResponse };
type ResponseWithData = SessionResponseData | SessionTestResponseData;

export type ResponseWithError = {
  error: FetchBaseQueryError | SerializedError;
};

/**
 * @param response from RTK Query.
 * @returns true
 * @throws Error if API or network error occurred.
 */
export const ensureSuccessfulSessionApiResponse = (
  response: ResponseWithData | ResponseWithError,
): response is ResponseWithData => {
  if ('error' in response) {
    const sessionApiError = (response.error as { data?: unknown }).data;
    if (isSessionApiError(sessionApiError)) {
      const errorAsString = formatSessionApiErrorToString(sessionApiError);
      throw new CustomError(`Error from server: ${errorAsString}`);
    }

    if ('status' in response.error) {
      const errorAsString = formatFetchBaseQueryError(response.error);
      throw new CustomError(`Error: ${errorAsString}`);
    }
  }
  if ('data' in response) {
    return true;
  } else {
    throw new CustomError('Error: No data in response');
  }
};
